import { campaignOverviewTitle, PageTitle } from '@/features/shared/page-title';
import { AiGenerateLineItemsButton } from '@/features/ai/line-items/ai-generate-line-items-button';
import { CampaignTopNav } from '@/features/proposals/shared-campaign-nav';
import {
  ExpandableSidebarSection,
  PageContentSection,
  PageSection
} from '@/components/layout/layout';
import { useProposalContext } from '@/features/proposals/proposal-context';
import {
  faCopy,
  faFileInvoice,
  faList,
  faTrashCan,
  faUser
} from '@fortawesome/pro-light-svg-icons';
import {
  CampaignDetailsRail,
  CampaignRoleAssignmentsRail
} from '@/components/display/details-rail';
import {
  isAgencyMarkupStrategy,
  isDefinedStrategy,
  isReferralCommissionStrategy
} from '@/features/strategy/strategy-utils';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { CreateProposalStrategy } from '@/features/proposals/create-strategy';
import { ProposalInfo, ProposalInfoProvider } from '@/features/proposals/proposal-info-context';
import React, { ReactNode, useState } from 'react';
import {
  EditLineItemFormSelectorV3,
  NewLineItemFormSelectorV3
} from '@/features/line-items/line-item-form/route/new-line-item-form-selector-v3';
import { ProposalTopSection } from '@/features/proposals/proposal-top-section';
import { TableResetButtons } from '@/components/tables/table-reset-buttons';
import { CopyPricingGridV2 } from '@/features/proposals/copy-pricing-grid-button';
import { ExportIOButton } from '@/features/proposals/proposal-export-button';
import { SyncIOToZohoButton } from '@/features/proposals/sync-io-to-zoho-button';
import { ExecuteProposalButton } from '@/features/proposals/promotion/execute-proposal-button';
import { NewLineItemButton } from '@/features/line-items/new-line-item-button';
import { ProposalLineItemsTable } from '@/features/proposals/proposal-line-items-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LineItemDeleteModalV2 } from '@/features/line-items/line-item-delete-modal';
import { clsx } from 'clsx';
import { Tooltip } from 'react-tooltip';
import { DBStrategy } from 'shared/src/types';
import { trpc } from '@/utils/trpc-client';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { FormEditState } from '@/features/line-items/line-item-form/common/form-types';
import { CampaignIOsRail } from '@/features/insertion-orders/campaign-ios-rail';
import { useFlagEnabled } from '@/utils/feature-flags';
import { useCaptureCampaignView } from '@/utils/posthog/analytics';

export function ProposalPage() {
  const { campaign } = useCampaignContext();
  useCaptureCampaignView();

  return (
    <div className="flex w-full flex-col">
      <PageTitle title={campaignOverviewTitle(campaign.campaign_name)} />
      <CampaignTopNav />
      <PageSection>
        <PageContentSection>
          <ProposalMainOuter />
        </PageContentSection>
        <Sidebar />
      </PageSection>
    </div>
  );
}

function Sidebar() {
  const { proposal } = useProposalContext();
  return (
    <ExpandableSidebarSection
      sections={[
        {
          name: 'details',
          header: 'Zoho Details',
          icon: faList,
          render: () => (
            <CampaignDetailsRail
              isStrategyDefined={isDefinedStrategy(proposal)}
              strategy={proposal}
            />
          )
        },
        {
          name: 'users',
          header: 'Role Assignments',
          icon: faUser,
          render: () => <CampaignRoleAssignmentsRail />
        },
        {
          name: 'ios',
          header: 'Campaign IOs',
          icon: faFileInvoice,
          render: () => <CampaignIOsRail />
        }
      ]}
    />
  );
}

function ProposalMainOuter() {
  const { proposal, lineItems } = useProposalContext();
  const strategyIsDefined = isDefinedStrategy(proposal);

  if (!strategyIsDefined) return <CreateProposalStrategy />;

  const proposalInfo = createProposalInfo(proposal, lineItems);
  return (
    <ProposalInfoProvider proposalInfo={proposalInfo}>
      <ProposalMain />
    </ProposalInfoProvider>
  );
}

function ProposalMain() {
  const { lineItems } = useProposalContext();
  const [formState, setFormState] = useState<FormEditState>({ state: 'closed' });

  const closeForm = () => {
    setFormState({ state: 'closed' });
  };
  const setIsAdding = () => setFormState({ state: 'create' });

  function setIsEditing(lineItemId: string) {
    const lineItem = lineItems.find(li => li.id === lineItemId);
    if (!lineItem) throw new Error('Line item not found');
    setFormState({ state: 'edit', lineItem });
  }

  // If the form is active, show it instead of the line items table
  switch (formState.state) {
    case 'create':
      return <NewLineItemFormSelectorV3 onClose={closeForm} />;
    case 'edit':
      return <EditLineItemFormSelectorV3 onClose={closeForm} lineItem={formState.lineItem} />;
    case 'closed':
      return (
        <>
          <ProposalTopSection />
          <LineItemsSection
            lineItems={lineItems}
            setIsAdding={setIsAdding}
            setIsEditing={setIsEditing}
          />
        </>
      );
  }
}

type LineItemsSectionProps = {
  lineItems: LineItemTrpcResponse[];
  setIsAdding: () => void;
  setIsEditing: (lineItemId: string) => void;
};

function LineItemsSection({ lineItems, setIsAdding, setIsEditing }: LineItemsSectionProps) {
  const handleEditLineItem = (lineItemId: string) => {
    if (lineItems.some(li => li.id === lineItemId)) setIsEditing(lineItemId);
  };
  const { campaign } = useCampaignContext();
  const [selectedRows, setSelectedRows] = React.useState<LineItemTrpcResponse[]>([]);
  const { mutateAsync, isPending } = trpc.duplicateLineItem.useMutation();
  const utils = trpc.useUtils();
  const hasCustomPacing = lineItems.some(li => li.pacing_type === 'custom');
  const assistMeEnabled = useFlagEnabled('assist-me');

  async function handleDuplicateLineItems(lineItems: LineItemTrpcResponse[]) {
    if (lineItems.length > 0) {
      if (isPending) return;
      for await (const lineItem of lineItems) {
        await mutateAsync({ strategy_id: lineItem.strategy_id, line_item_id: lineItem.id });
      }
      await utils.getProposalLineItems.invalidate();
      await utils.proposalDiff.invalidate();
    }
  }

  async function handleBulkDuplicate() {
    await handleDuplicateLineItems(selectedRows);
  }

  async function handleKeyboardDuplicate(currentRow: LineItemTrpcResponse) {
    const otherRows = selectedRows.filter(row => row.id !== currentRow.id);
    await handleDuplicateLineItems([...otherRows, currentRow]);
  }

  const disableDelete = false; // TODO[mk] Don't allow delete if the lineitem already has an execution

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 py-2">
        <LineItemsCount length={lineItems.length} />
        <div className="flex flex-wrap items-center justify-end gap-2">
          {selectedRows.length > 0 ? (
            <>
              <DuplicateButton handleDuplicate={handleBulkDuplicate} />
              <DeleteButton disabled={disableDelete} selectedRows={selectedRows} />
            </>
          ) : (
            <>
              <TableResetButtons />
              <CopyPricingGridV2 />
              <SyncIOToZohoButton />
              <ExportIOButton
                campaignId={campaign.campaign_id}
                strategyId={campaign.proposal_id}
                hasCustomPacing={hasCustomPacing}
              />
              <ExecuteProposalButton />
              {assistMeEnabled ? (
                <AiGenerateLineItemsButton
                  proposalId={campaign.proposal_id}
                  campaignName={campaign.campaign_name}
                  onEditLineItem={handleEditLineItem}
                />
              ) : null}
              <NewLineItemButton setIsAdding={setIsAdding} />
            </>
          )}
        </div>
      </div>
      <ProposalLineItemsTable
        lineItems={lineItems}
        setIsEditing={setIsEditing}
        setSelectedRows={setSelectedRows}
        handleKeyboardDuplicate={handleKeyboardDuplicate}
        hideRowActions={selectedRows.length > 0}
      />
    </div>
  );
}

function LineItemsCount(props: { length: number }) {
  return (
    <div className="flex items-center gap-4">
      <div className="text-lg font-bold">Line Items</div>
      <div className="rounded bg-blue-100 px-2 py-0.5 font-semibold text-gray-600">
        {props.length}
      </div>
    </div>
  );
}

function DuplicateButton({ handleDuplicate }: { handleDuplicate: () => void }) {
  return (
    <LineItemBulkAction
      colorClasses="text-blue-600 hover:bg-blue-50"
      icon={<FontAwesomeIcon icon={faCopy} />}
      onClick={handleDuplicate}
      label="Duplicate"
    />
  );
}

type DeleteButtonProps = {
  disabled: boolean;
  selectedRows: LineItemTrpcResponse[];
};

function DeleteButton({ disabled, selectedRows }: DeleteButtonProps) {
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  return (
    <>
      <LineItemBulkAction
        colorClasses="text-red-600 hover:bg-red-50"
        disabled={disabled}
        icon={<FontAwesomeIcon icon={faTrashCan} />}
        onClick={() => setModalOpen(true)}
        label="Delete"
        tooltipId="delete-line-item-tooltip"
      />
      {disabled && <DeleteDisabledTooltip />}
      <LineItemDeleteModalV2
        open={modalOpen}
        setOpen={setModalOpen}
        lineItemsToDelete={selectedRows}
      />
    </>
  );
}

type LineItemBulkActionProps = {
  colorClasses: string;
  disabled?: boolean;
  icon: ReactNode;
  onClick: () => void;
  label: string;
  tooltipId?: string;
};

function LineItemBulkAction({
  colorClasses,
  disabled = false,
  icon,
  onClick,
  label,
  tooltipId
}: LineItemBulkActionProps) {
  return (
    <div>
      <button
        data-tooltip-id={tooltipId}
        className={clsx(
          `flex cursor-pointer items-center gap-2 rounded-md px-3 py-1 text-sm disabled:cursor-not-allowed ${!disabled ? colorClasses : ''}`,
          disabled ? 'text-gray-400' : ''
        )}
        disabled={disabled}
        onClick={onClick}>
        {icon}
        <div>{label}</div>
      </button>
    </div>
  );
}

function DeleteDisabledTooltip() {
  return (
    <Tooltip
      id="delete-line-item-tooltip"
      place="left-start"
      style={{
        backgroundColor: '#111827',
        borderRadius: '5px',
        maxWidth: 215,
        opacity: 1,
        position: 'fixed',
        zIndex: 1
      }}>
      <div className="text-sm">
        <div className="font-semibold">Cannot delete line items with existing Media Plans.</div>
        <div>One or more selected line items has media plans.</div>
      </div>
    </Tooltip>
  );
}

function createProposalInfo(proposal: DBStrategy, lineItems: LineItemTrpcResponse[]): ProposalInfo {
  if (!proposal.start_date || !proposal.end_date)
    throw new Error('Strategy must have start and end dates to create a line item');

  return {
    campaign_dates: { from: proposal.start_date, to: proposal.end_date },
    planned_margin: 0, // TODO[mk] - Update proposals API to include margins
    target_margin: 0,
    proposal_price: proposal.price || 0,
    allocated_price: lineItems.map(li => li.price).reduce((a, b) => a + b, 0) || 0,
    is_agency_markup: isAgencyMarkupStrategy(proposal),
    is_referral_commission: isReferralCommissionStrategy(proposal),
    agency_markup_rate: proposal.agency_markup_rate,
    referral_commission_rate: proposal.referral_commission_rate
  };
}
